/* eslint-disable max-lines-per-function, max-statements */
import { getClientTimeout } from './clientTimeoutConfig';

type FetchInput = Parameters<typeof globalThis.fetch>[0];
type FetchInit = Parameters<typeof globalThis.fetch>[1];
type FetchResponse = Awaited<ReturnType<typeof globalThis.fetch>>;

export type ApiErrorKind = 'api' | 'network' | 'timeout' | 'cancelled' | 'unknown';

interface AxiosLikeError {
  isAxiosError: true;
  code?: string;
  response?: {
    status: number;
    data: unknown;
    headers?: Record<string, unknown>;
  };
}

export interface ApiFieldError {
  field: string;
  code: string;
  message: string;
}

interface ErrorEnvelope {
  error?: {
    code?: unknown;
    message?: unknown;
    status?: unknown;
    retryable?: unknown;
    retry_after_seconds?: unknown;
    field_errors?: unknown;
  };
  detail?: unknown;
  message?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status?: number;
  readonly retryable: boolean;
  readonly retryAfterSeconds?: number;
  readonly fieldErrors: ApiFieldError[];
  readonly kind: ApiErrorKind;

  constructor(options: {
    code: string;
    message: string;
    status?: number;
    retryable?: boolean;
    retryAfterSeconds?: number;
    fieldErrors?: ApiFieldError[];
    kind?: ApiErrorKind;
  }) {
    super(options.message);
    this.name = 'ApiError';
    this.code = options.code;
    this.status = options.status;
    this.retryable = options.retryable ?? false;
    this.retryAfterSeconds = options.retryAfterSeconds;
    this.fieldErrors = options.fieldErrors ?? [];
    this.kind = options.kind ?? 'api';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isAxiosError = (value: unknown): value is AxiosLikeError => (
  isRecord(value) && value.isAxiosError === true
);

const isCancelled = (value: unknown): boolean => (
  isRecord(value) && value.__CANCEL__ === true
);

const text = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim() ? value.trim() : undefined
);

const number = (value: unknown): number | undefined => (
  typeof value === 'number' && Number.isFinite(value) ? value : undefined
);

const legacyDetailMessage = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') return text(detail);
  if (isRecord(detail)) return text(detail.message) || text(detail.detail);
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (isRecord(item) ? text(item.msg) || text(item.message) : undefined))
      .filter((item): item is string => !!item);

    return messages.length ? messages.join(' ') : undefined;
  }

  return undefined;
};

const defaultMessage = (status?: number): string => {
  if (status === 401) return 'Your session has expired. Sign in again to continue.';
  if (status === 403) return 'You don’t have permission to do that.';
  if (status === 404) return 'The requested item could not be found.';
  if (status === 409) return 'That change conflicts with the latest data. Refresh and try again.';
  if (status === 422) return 'Check the information you entered and try again.';
  if (status === 429) return 'You’ve made too many requests. Wait a moment and try again.';
  if (status && status >= 500) return 'The service couldn’t complete your request. Try again shortly.';

  return 'We couldn’t complete your request. Try again.';
};

const parseFieldErrors = (value: unknown): ApiFieldError[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const field = text(item.field);
    const message = text(item.message);

    if (!field || !message) return [];

    return [{ field, message, code: text(item.code) || 'INVALID_VALUE' }];
  });
};

const fromEnvelope = (data: unknown, fallbackStatus?: number, retryAfterHeader?: string): ApiError => {
  const envelope: ErrorEnvelope = isRecord(data) ? data : {};
  const canonical = isRecord(envelope.error) ? envelope.error : {};
  const status = number(canonical.status) ?? fallbackStatus;
  const retryAfter = number(canonical.retry_after_seconds)
    ?? (retryAfterHeader && /^\d+$/.test(retryAfterHeader) ? Number(retryAfterHeader) : undefined);

  return new ApiError({
    code: text(canonical.code) || (status ? `HTTP_${status}` : 'REQUEST_FAILED'),
    message: text(canonical.message)
      || legacyDetailMessage(envelope.detail)
      || text(envelope.message)
      || defaultMessage(status),
    status,
    retryable: typeof canonical.retryable === 'boolean'
      ? canonical.retryable
      : status === 429 || status === 502 || status === 503 || status === 504,
    retryAfterSeconds: retryAfter,
    fieldErrors: parseFieldErrors(canonical.field_errors),
  });
};

const fromSerializedError = (value: Record<string, unknown>): ApiError | undefined => {
  const data = isRecord(value.data) ? value.data : undefined;

  if (!data || !text(data.message)) return undefined;
  const rawStatus = value.status;
  const status = typeof rawStatus === 'number' ? rawStatus : undefined;

  return new ApiError({
    code: text(data.code) || (status ? `HTTP_${status}` : 'REQUEST_FAILED'),
    message: text(data.message) as string,
    status,
    retryable: data.retryable === true,
    retryAfterSeconds: number(data.retryAfterSeconds),
    fieldErrors: parseFieldErrors(data.fieldErrors),
  });
};

export const normalizeApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) return error;

  if (isRecord(error)) {
    const serialized = fromSerializedError(error);

    if (serialized) return serialized;
  }

  if (isCancelled(error) || (error instanceof globalThis.DOMException && error.name === 'AbortError')) {
    return new ApiError({ code: 'REQUEST_CANCELLED', message: 'The request was cancelled.', kind: 'cancelled' });
  }

  if (error instanceof TypeError && /fetch|network|load/i.test(error.message)) {
    const online = typeof navigator === 'undefined' || navigator.onLine;

    return new ApiError({
      code: online ? 'NETWORK_UNAVAILABLE' : 'NETWORK_OFFLINE',
      message: online
        ? 'We couldn’t reach the service. Check your connection and try again.'
        : 'You appear to be offline. Reconnect and try again.',
      retryable: true,
      kind: 'network',
    });
  }

  if (isAxiosError(error)) {
    const axiosError = error;

    if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
      return new ApiError({
        code: 'REQUEST_TIMEOUT',
        message: 'The request took too long. Check your connection and try again.',
        retryable: true,
        kind: 'timeout',
      });
    }
    if (!axiosError.response) {
      return new ApiError({
        code: typeof navigator === 'undefined' || navigator.onLine ? 'NETWORK_UNAVAILABLE' : 'NETWORK_OFFLINE',
        message: typeof navigator === 'undefined' || navigator.onLine
          ? 'We couldn’t reach the service. Check your connection and try again.'
          : 'You appear to be offline. Reconnect and try again.',
        retryable: true,
        kind: 'network',
      });
    }

    return fromEnvelope(
      axiosError.response.data,
      axiosError.response.status,
      text(axiosError.response.headers?.['retry-after']),
    );
  }

  if (error instanceof Error && error.message) {
    return new ApiError({ code: 'UNEXPECTED_CLIENT_ERROR', message: error.message, kind: 'unknown' });
  }

  return new ApiError({ code: 'UNEXPECTED_CLIENT_ERROR', message: defaultMessage(), kind: 'unknown' });
};

export const errorMessage = (error: unknown, fallback?: string): string => {
  const normalized = normalizeApiError(error);
  const validationMessages = normalized.fieldErrors
    .map((fieldError) => fieldError.message)
    .filter((message, index, messages) => messages.indexOf(message) === index);

  if (normalized.kind === 'unknown' && fallback) return fallback;
  if (validationMessages.length) return validationMessages.join(' ');

  return normalized.message || fallback || defaultMessage(normalized.status);
};

export interface SerializedApiError {
  status: number | 'FETCH_ERROR';
  data: {
    code: string;
    message: string;
    retryable: boolean;
    retryAfterSeconds?: number;
    fieldErrors: ApiFieldError[];
  };
}

export const serializeApiError = (error: unknown): SerializedApiError => {
  const normalized = normalizeApiError(error);

  return {
    status: normalized.status ?? 'FETCH_ERROR',
    data: {
      code: normalized.code,
      message: normalized.message,
      retryable: normalized.retryable,
      retryAfterSeconds: normalized.retryAfterSeconds,
      fieldErrors: normalized.fieldErrors,
    },
  };
};

export const responseError = async (response: FetchResponse): Promise<ApiError> => {
  let payload: unknown;

  try {
    payload = await response.clone().json();
  } catch {
    payload = undefined;
  }

  return fromEnvelope(payload, response.status, response.headers.get('Retry-After') || undefined);
};

const wait = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

/**
 * The backoff steps shared by axios safe retries and fetchWithRetry, read from the
 * admin timeout panel each time a retry is scheduled (compiled-in: 500 ms, then 1500 ms).
 */
const retryBackoffSteps = (): number[] => [
  getClientTimeout('apiRetryBackoffMs'),
  getClientTimeout('apiRetryBackoffMaxMs'),
];

/** Backoff before retry `attempt` (0-based); attempts past the table reuse its last step. */
export const retryBackoffMilliseconds = (attempt: number): number => {
  const steps = retryBackoffSteps();

  return steps[Math.min(Math.max(attempt, 0), steps.length - 1)];
};

const retryDelay = (normalized: ApiError, attempt: number): number => (
  (normalized.retryAfterSeconds ? normalized.retryAfterSeconds * 1000 : retryBackoffMilliseconds(attempt))
  + Math.floor(Math.random() * 150)
);

export async function fetchWithRetry(
  input: FetchInput,
  init: FetchInit = {},
): Promise<FetchResponse> {
  const method = (init.method || 'GET').toUpperCase();
  const canRetry = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
  const maxRetries = getClientTimeout('fetchRetryAttempts');

  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await globalThis.fetch(input, init);

      if (!canRetry || response.ok) return response;
      const normalized = await responseError(response);

      if (!normalized.retryable || attempt >= maxRetries) return response;
      await wait(retryDelay(normalized, attempt));
    } catch (error) {
      const normalized = normalizeApiError(error);

      if (!canRetry || !normalized.retryable || attempt >= maxRetries) throw normalized;
      await wait(retryDelay(normalized, attempt));
    }
  }
}

export async function fetchJson<T>(input: FetchInput, init: FetchInit = {}): Promise<T> {
  const response = await fetchWithRetry(input, init);

  if (!response.ok) throw await responseError(response);
  if (response.status === 204) return undefined as T;

  return await response.json() as T;
}
